import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { testimonials } from "@/mocks/guaData";

const SITE_URL = "https://hshplx.cn";

const principles = [
  {
    icon: "ri-file-list-3-line",
    title: "只做脉络与归纳",
    text: "不转载原始图文，不搬运他人观点。我们把公开讨论拆成时间线，说清楚发生了什么、各方在意什么。",
  },
  {
    icon: "ri-shield-check-line",
    title: "未证实就标注",
    text: "尚未有官方回应的说法，会在正文中明确提醒。宁可写得慢一点，也不把猜测当结论。",
  },
  {
    icon: "ri-edit-2-line",
    title: "有错就改",
    text: "收到有效反馈后重新核实，更正内容并保留更新记录，而不是悄悄替换掉原来的说法。",
  },
  {
    icon: "ri-user-heart-line",
    title: "不鼓励站队",
    text: "榜单呈现的是讨论热度，不是价值判断。我们不引导情绪，尽量把选择权留给读者。",
  },
];

const milestones = [
  { step: "01", title: "收录讨论", text: "从公开渠道发现正在发酵的话题，判断是否具备持续讨论价值。" },
  { step: "02", title: "梳理脉络", text: "按时间顺序还原事件发展，把关键节点和争议点单独标出。" },
  { step: "03", title: "分类归档", text: "归入对应主题分类，方便只想看某一类内容的读者快速定位。" },
  { step: "04", title: "榜单更新", text: "根据讨论增速调整排名，热度回落的话题转入热点盘点继续留存。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 瓜田热榜的内容原则与联系方式",
    description:
      "了解瓜田热榜的网站定位、发展理念与内容原则：只做公开讨论的脉络综述，未证实内容明确标注，有误必改。同时提供反馈联系方式与读者口碑。",
    keywords: "关于瓜田热榜,热门吃瓜内容原则,吃瓜热点聚合,联系方式,全网热议",
    canonical: `${SITE_URL}/about`,
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 瓜田热榜",
        url: `${SITE_URL}/about`,
        inLanguage: "zh-CN",
        description: "瓜田热榜的网站定位、发展理念、内容原则与联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "瓜田热榜",
        url: `${SITE_URL}/`,
        email: "hello@guatianrebang.example.com",
        description: "全网热议话题聚合平台，汇总今日热门吃瓜榜与最近吃瓜热点盘点。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
                  <span className="h-px w-6 bg-primary-400" />
                  关于我们
                </p>
                <h1 className="heading-font mt-3 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                  把散落各处的讨论，整理成一条看得懂的脉络
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  瓜田热榜是一个面向中文互联网用户的吃瓜热点聚合平台。我们不生产八卦，
                  只负责把同一件事在各个平台上零散的讨论收拢起来，按时间和争议点重新排好，
                  让你不用翻几十页评论也能知道事情到底发展到了哪一步。
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/rank"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    看看今天的榜单
                  </Link>
                  <Link
                    to="/#categories"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    浏览主题分类
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5">
                <dl className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <dt className="text-xs text-foreground-500">主题分类</dt>
                    <dd className="heading-font mt-1.5 text-2xl font-black text-foreground-950">6</dd>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <dt className="text-xs text-foreground-500">榜单刷新</dt>
                    <dd className="heading-font mt-1.5 text-2xl font-black text-foreground-950">
                      1 小时
                    </dd>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <dt className="text-xs text-foreground-500">今日收录</dt>
                    <dd className="heading-font mt-1.5 text-2xl font-black text-foreground-950">1,286</dd>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <dt className="text-xs text-foreground-500">更正响应</dt>
                    <dd className="heading-font mt-1.5 text-2xl font-black text-foreground-950">24 小时</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50" aria-labelledby="mission-title">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-20">
            <SectionHeading
              eyebrow="网站定位"
              title="我们想解决的是什么问题"
              description="热搜让人知道发生了什么，却很少告诉你为什么在吵。我们补的正是这一块。"
            />
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
                <h3 className="heading-font text-lg font-bold text-foreground-950">发展理念</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  热点是流动的，今天的榜一明天可能无人提起。所以我们既看累计热度，更看讨论增速，
                  让榜单保持“此刻正在发生”的感觉，同时把退烧的话题留在盘点里，方便回溯。
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  在信息密度和阅读体验之间，我们选择克制：不堆满弹窗，不用夸张标题，
                  每一条话题都给出足够判断的上下文。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
                <h3 className="heading-font text-lg font-bold text-foreground-950">内容是怎么来的</h3>
                <ol className="mt-4 flex flex-col gap-4">
                  {milestones.map((item) => (
                    <li key={item.step} className="flex gap-4">
                      <span className="heading-font flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-sm font-black text-secondary-800">
                        {item.step}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground-900">{item.title}</span>
                        <span className="mt-1 text-sm leading-relaxed text-foreground-600">{item.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section
          id="principles"
          className="w-full scroll-mt-28 bg-background-100/60"
          aria-labelledby="principles-title"
        >
          <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-20">
            <SectionHeading
              eyebrow="内容原则"
              title="我们给自己定的四条规矩"
              description="吃瓜可以轻松，但整理信息的人不能随便。"
            />
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item) => (
                <article
                  key={item.title}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl leading-none`} aria-hidden="true" />
                  </span>
                  <h3 className="heading-font mt-4 text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50" aria-labelledby="voice-title">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-20">
            <SectionHeading eyebrow="用户口碑" title="读者怎么说" description="来自不同使用场景的反馈。" />
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((item) => (
                <figure
                  key={item.name}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-100/60 p-5"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-sm font-bold text-secondary-800">
                    {item.initial}
                  </span>
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                    “{item.text}”
                  </blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-3 text-sm">
                    <span className="font-semibold text-foreground-900">{item.name}</span>
                    <span className="ml-2 text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-secondary-900" aria-labelledby="contact-title">
          <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-8 px-4 py-12 md:grid-cols-3 md:px-8 md:py-16">
            <div className="md:col-span-2">
              <h2 id="contact-title" className="heading-font text-2xl font-black text-background-50 md:text-3xl">
                发现内容有误，或者想推荐一个话题？
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-50/75 md:text-base">
                欢迎直接发邮件给我们。涉及事实错误的反馈，我们会重新核实并在 24 小时内处理；
                话题推荐则会进入编辑的日常选题池，被采纳后会在盘点里出现。
              </p>
            </div>
            <div className="flex flex-col justify-center gap-3">
              <a
                href="mailto:hello@guatianrebang.example.com"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-mail-send-line text-base leading-none" aria-hidden="true" />
                </span>
                hello@guatianrebang.example.com
              </a>
              <Link
                to="/rank"
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:border-accent-300 hover:text-accent-200"
              >
                返回今日热榜
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}